import { captureException } from '@sentry/react';
import type { NoddiAsyncError } from 'noddi-types';
import { getApiErrorSeverity, getNetworkErrorExtra, isAxiosError, logToConsole } from 'noddi-util';
import { log } from './logUtil';

/**
 * Shared query/mutation reporting; each app keeps its own toast policy.
 * API context includes request params/body and the backend response.
 */
export const logQueryCacheError = (kind: 'query' | 'mutation', error: unknown) => {
  // A programming error has no HTTP status either; only Axios failures may
  // use the offline/cancellation filters. Preserve the original error stack.
  if (!isAxiosError(error)) {
    if (import.meta.env.MODE !== 'production') {
      logToConsole({ value: error, severity: 'error' });
      return;
    }

    // Give Sentry the original object: its identity check also deduplicates
    // a later unhandled rejection, and its serializer handles circular values.
    // Primitives have no identity; never suppress unrelated rejections by value.
    const exception =
      (typeof error === 'object' && error !== null) || typeof error === 'function'
        ? error
        : new Error(`Non-Error rejection on ${kind}: ${String(error)}`);
    captureException(exception, { tags: { queryKind: kind } });
    return;
  }

  const noddiAsyncError = error as NoddiAsyncError;
  log({
    value: `Network error on ${kind}, status code: ${noddiAsyncError.status}`,
    severity: getApiErrorSeverity(noddiAsyncError),
    cause: noddiAsyncError,
    extra: getNetworkErrorExtra(noddiAsyncError)
  });
};
